import * as XLSX from 'xlsx';

import { IProductImportService } from './IProductImportService';
import { ProductImportRow } from '../dto/ProductImportRow.dto';
import { ProductImportResult } from '../dto/ProductImportResult';
import { ProductImportItem } from '../dto/ProductImportItem.type';
import type { IProductInventoryService } from '../productInventory/IProductInventoryService';
import type { ProductInventoryCreatePayload } from '../dto';
import { productInventoryService as defaultProductInventoryService } from '../index';
import { ProductImportCommitPayload } from '../dto/ProductImportCommitPayload';
import { ProductImportCommitResult } from '../dto/ProductImportCommitResult';
import { ListParams } from '..';
import WarehouseProductCategoryService from '../ProductCategory/ProductCategoryService';
import { ProductService } from '../../../service/ProductService';
// @ts-ignore
import productImportTemplateUrl from '../../../assets/templates/product_import_template_exported.xlsx';

type ImportPayload =
    | File
    | FormData
    | { file?: File }
    | { files?: File[] }
    | { target?: { files?: FileList } };

type CategoryOption = {
    uuid: string;
    name: string;
    namePl: string;
    categorySource: 'LEGACY' | 'WAREHOUSE' | 'MERGED';
    legacyCategoryUuid?: string;
    warehouseCategoryUuid?: string;
    component?: boolean;
};

type ImportedProductRow = ProductImportRow & {
    categorySource?: CategoryOption['categorySource'];
};

type ReturnablePackagingOption = {
    uuid: string;
    label: string;
    labelPl: string;
    price: number | null;
};

type DictionaryOption = {
    id: string;
    label: string;
};

type ImportDictionaries = {
    categories: CategoryOption[];
    returnablePackagingOptions: ReturnablePackagingOption[];
    unitOfMeasurementOptions: DictionaryOption[];
    vatCodeOptions: DictionaryOption[];
    currencyOptions: DictionaryOption[];
    statusOptions: DictionaryOption[];
};

type ExistingProductMatch = {
    exists: boolean;
    externalId?: string;
};

const roundMoney = (value: number): number =>
    Math.round((value + Number.EPSILON) * 100) / 100;

// Must stay in sync with assets/templates/product_import_template_exported.xlsx
// (Słowniki sheet). The template only bakes in the "Kategoria" *header*
// (F1) - every data row is created on the fly in injectCategoriesIntoTemplate
// so that only rows with a real category get the zebra-striped fill.
// Un-injected rows stay completely blank, matching how every other
// dictionary column already behaves (fill stops where its data stops).
const TEMPLATE_CATEGORY_DICTIONARY_SHEET_PATH = 'xl/worksheets/sheet2.xml';
const TEMPLATE_CATEGORY_COLUMN = 'F';
const TEMPLATE_CATEGORY_FIRST_ROW = 2;
// The two alternating style indices used by the sheet's own zebra
// striping are NOT hardcoded here on purpose. Every time a workbook gets
// resaved by a different tool (LibreOffice, Excel repairing a file, etc.)
// the whole cellXfs style table can get renumbered - a literal index that
// meant "light lavender fill" in one save of the template can silently
// mean something else entirely (e.g. the bold header style) in the next.
// buildCategoryDictionarySheetXml instead reads the *actual* style index
// off the template's own A2/A3 cells at injection time and reuses
// whatever it finds, so this keeps working no matter how the template
// asset gets re-saved.
// The 4 note rows that currently sit right below the dictionary columns
// (see the Słowniki sheet). If the category list is longer than the gap
// between row 2 and the first note row, the notes are pushed down to make
// room instead of truncating the category list.
const TEMPLATE_NOTE_ROWS = [25, 26, 27, 28];
// The Produkty sheet's "Kategoria" column (D) dropdown reads its option
// list from the Słowniki!F range above. The template ships with a
// single-cell placeholder ($F$2:$F$2) for that range - it gets widened
// here to match however many category names actually get written, so the
// dropdown doesn't include blank rows (or, worse, the notes block) and
// isn't missing real categories either.
const TEMPLATE_PRODUCT_SHEET_PATH = 'xl/worksheets/sheet1.xml';
const TEMPLATE_CATEGORY_VALIDATION_FORMULA_PATTERN =
    /(<dataValidation\b[^>]*\bsqref="D7:D1006"[^>]*>\s*<formula1>)Słowniki!\$F\$2:\$F\$\d+(<\/formula1>)/;
const XLSX_MIME_TYPE =
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const IMPORT_ERRORS = {
    missingProductName: 'products.importDialog.errors.missingProductName',
    missingEanOrExternalId:
        'products.importDialog.errors.missingEanOrExternalId',

    missingCategory: 'products.importDialog.errors.missingCategory',
    categoryNotFound: 'products.importDialog.errors.categoryNotFound',

    missingUnitOfMeasurement:
        'products.importDialog.errors.missingUnitOfMeasurement',
    unitOfMeasurementNotFound:
        'products.importDialog.errors.unitOfMeasurementNotFound',

    missingCurrency: 'products.importDialog.errors.missingCurrency',
    currencyNotFound: 'products.importDialog.errors.currencyNotFound',

    missingVatCode: 'products.importDialog.errors.missingVatCode',
    vatCodeNotFound: 'products.importDialog.errors.vatCodeNotFound',

    statusNotFound: 'products.importDialog.errors.statusNotFound',

    invalidPrice: 'products.importDialog.errors.invalidPrice',

    returnablePackagingTypeRequired:
        'products.importDialog.errors.returnablePackagingTypeRequired',
    returnablePackagingTypeNotFound:
        'products.importDialog.errors.returnablePackagingTypeNotFound',

    duplicateInFile: 'products.importDialog.errors.duplicateInFile',
    failedToImportProducts:
        'products.importDialog.errors.failedToImportProducts',
};

export class ProductImportService implements IProductImportService {
    private importedRows: ProductImportRow[] = [];

    constructor(
        private readonly productInventoryService: IProductInventoryService = defaultProductInventoryService,
    ) {}

    async importData(payload: ImportPayload): Promise<ProductImportResult> {
        const file = this.extractFile(payload);

        if (!file) {
            return this.createImportResult([]);
        }

        const dictionaries = await this.fetchImportDictionaries();

        this.importedRows = await this.parseFile(file, dictionaries);

        return this.createImportResult(this.importedRows);
    }

    async list(): Promise<ProductImportResult> {
        return this.createImportResult(this.importedRows);
    }

    async commitImport(
        payload?: ProductImportCommitPayload,
    ): Promise<ProductImportCommitResult> {
        const replaceExisting = Boolean(payload?.replaceExisting);

        const rows = (
            payload?.products?.length ? payload.products : this.importedRows
        ).filter((row) => !(row.errors ?? []).length);

        let imported = 0;

        try {
            for (const row of rows) {
                const productPayload = this.createProductPayload(row);

                if (replaceExisting && row.existingProductExternalId) {
                    await this.productInventoryService.update(
                        row.existingProductExternalId,
                        productPayload,
                    );
                } else {
                    await this.productInventoryService.create(productPayload);
                }

                imported += 1;
            }

            return { success: true, imported };
        } catch (error) {
            console.error('ProductImportService::commitImport error', error);

            return {
                success: false,
                imported,
                errorMessage: IMPORT_ERRORS.failedToImportProducts,
            };
        }
    }

    private createProductPayload(
        row: ProductImportRow,
    ): ProductInventoryCreatePayload {
        const importedRow = row as ImportedProductRow;
        const hasReturnablePackaging =
            row.returnablePackaging === true && !!row.returnablePackagingUuid;

        const finalPrice =
            row.price === null || row.price === undefined
                ? row.price
                : roundMoney(
                      row.price +
                          (hasReturnablePackaging
                              ? (row.returnablePackagingPrice ?? 0)
                              : 0),
                  );

        const categoryReference =
            importedRow.categorySource === 'WAREHOUSE'
                ? {
                      warehouseCategory: {
                          uuid: row.categoryUuid,
                      },
                  }
                : {
                      category: {
                          uuid: row.categoryUuid,
                      },
                  };

        return {
            externalSystemId: row.externalId || undefined,
            ean: row.ean?.trim() || null,
            name: row.name,
            ...categoryReference,
            weightVolume: row.weightVolume,
            unitOfMeasurement: row.unitOfMeasurement,
            minQuantity: row.minQuantity ?? 0,
            maxQuantity: row.maxQuantity ?? 100,
            price: finalPrice,
            netPrice: null,
            vatCode: row.vatCode,
            vatRate: null,
            currency: row.currency,
            status: row.status || 'ACTIVE',
            returnablePackaging:
                row.returnablePackaging === true && row.returnablePackagingUuid
                    ? { uuid: row.returnablePackagingUuid }
                    : null,
            returnablePackagingPrice: hasReturnablePackaging
                ? row.returnablePackagingPrice
                : null,
            useAsComponent: false,
            includeComponents: false,
        } as ProductInventoryCreatePayload;
    }

    private async createImportResult(
        rows: ProductImportRow[],
    ): Promise<ProductImportResult> {
        const valid: ProductImportItem[] = [];
        const duplicates: ProductImportItem[] = [];
        const conflicts: ProductImportItem[] = [];
        const invalid: ProductImportItem[] = [];

        const fileDuplicateKeys = this.findFileDuplicateKeys(rows);

        for (let index = 0; index < rows.length; index += 1) {
            const row = rows[index];

            const item: ProductImportItem = {
                rowIndex: index + 1,
                dto: row,
                errors: row.errors ?? [],
            };

            if ((row.errors ?? []).length) {
                invalid.push(item);
                continue;
            }

            const collisionKey = this.getCollisionKey(row);
            const existingProduct = await this.findExistingProduct(row);

            if (collisionKey && fileDuplicateKeys.has(collisionKey)) {
                if (existingProduct.exists && existingProduct.externalId) {
                    row.existingProductExternalId = existingProduct.externalId;
                }

                conflicts.push({
                    ...item,
                    conflictKey: collisionKey,
                    errors: [
                        ...(row.errors ?? []),
                        this.translationError(IMPORT_ERRORS.duplicateInFile, {
                            key: collisionKey,
                        }),
                    ],
                });
                continue;
            }

            if (existingProduct.exists) {
                row.existingProductExternalId = existingProduct.externalId;
                row.importStatus = 'EXISTS';

                duplicates.push({
                    ...item,
                    duplicateKey: row.ean || row.externalId,
                });
                continue;
            }

            valid.push(item);
        }

        return {
            valid,
            duplicates,
            conflicts,
            invalid,
            totalRecords: rows.length,
        };
    }

    private async fetchImportDictionaries(): Promise<ImportDictionaries> {
        const [
            categories,
            returnablePackagingOptions,
            unitOfMeasurementOptions,
            vatCodeOptions,
            currencyOptions,
            statusOptions,
        ] = await Promise.all([
            this.fetchCategories(),
            this.fetchReturnablePackagingOptions(),
            this.fetchUnitOfMeasurementOptions(),
            this.fetchVatCodeOptions(),
            this.fetchCurrencyOptions(),
            this.fetchStatusOptions(),
        ]);

        return {
            categories,
            returnablePackagingOptions,
            unitOfMeasurementOptions,
            vatCodeOptions,
            currencyOptions,
            statusOptions,
        };
    }

    private async fetchCategories(): Promise<CategoryOption[]> {
        const [legacyResult, warehouseResult] = await Promise.allSettled([
            this.fetchReferenceCollection('getCategoryNames'),
            this.fetchAllWarehouseCategories(),
        ]);

        if (
            legacyResult.status === 'rejected' &&
            warehouseResult.status === 'rejected'
        ) {
            console.error('ProductImportService::fetchCategories error', {
                legacyError: legacyResult.reason,
                warehouseError: warehouseResult.reason,
            });
            return [];
        }

        if (legacyResult.status === 'rejected') {
            console.error(
                'ProductImportService::fetchLegacyCategories error',
                legacyResult.reason,
            );
        }

        if (warehouseResult.status === 'rejected') {
            console.error(
                'ProductImportService::fetchWarehouseCategories error',
                warehouseResult.reason,
            );
        }

        const legacyCategories =
            legacyResult.status === 'fulfilled' &&
            Array.isArray(legacyResult.value)
                ? legacyResult.value
                      .map((category) => this.mapLegacyCategory(category))
                      .filter(
                          (category): category is CategoryOption =>
                              category !== null &&
                              category.component !== true,
                      )
                : [];

        const warehouseCategories =
            warehouseResult.status === 'fulfilled'
                ? warehouseResult.value
                      .map((category) => this.mapWarehouseCategory(category))
                      .filter(
                          (category): category is CategoryOption =>
                              category !== null,
                      )
                : [];

        return this.mergeCategories(legacyCategories, warehouseCategories);
    }

    private async fetchReferenceCollection(
        methodName:
            | 'getCategoryNames'
            | 'getUnitOfMeasurementList'
            | 'getVatCodeList'
            | 'getCurrencyList'
            | 'getStatusList',
    ): Promise<unknown[]> {
        const errors: unknown[] = [];

        try {
            const service = ProductService as unknown as Record<
                string,
                unknown
            >;
            const method = service[methodName];

            if (typeof method === 'function') {
                const response = await (
                    method as (...args: unknown[]) => Promise<unknown>
                ).call(ProductService);
                const values = this.extractCollection(response);

                if (values.length) return values;
            }
        } catch (error) {
            errors.push(error);
        }

        try {
            const service = this.productInventoryService as unknown as Record<
                string,
                unknown
            >;
            const method = service[methodName];

            if (typeof method === 'function') {
                const response = await (
                    method as (...args: unknown[]) => Promise<unknown>
                ).call(this.productInventoryService);
                const values = this.extractCollection(response);

                if (values.length) return values;
            }
        } catch (error) {
            errors.push(error);
        }

        if (errors.length) {
            console.error(
                `ProductImportService::${methodName} failed for all sources`,
                errors,
            );
        }

        return [];
    }

    private extractCollection(value: unknown, depth = 0): unknown[] {
        if (Array.isArray(value)) return value;
        if (!value || typeof value !== 'object' || depth >= 3) return [];

        const record = value as Record<string, unknown>;

        for (const key of [
            'content',
            'data',
            'items',
            'values',
            'results',
        ]) {
            if (!(key in record)) continue;

            const nested = this.extractCollection(record[key], depth + 1);
            if (nested.length) return nested;
        }

        return [];
    }

    private async fetchAllWarehouseCategories(): Promise<unknown[]> {
        const categories: unknown[] = [];
        let page = 1;
        let totalPages = 1;

        do {
            const response = await WarehouseProductCategoryService.getList(
                new ListParams({
                    page,
                    size: 100,
                    sort: 'name:asc',
                }),
            );

            categories.push(...this.extractCollection(response));

            totalPages = this.extractTotalPages(response);

            page += 1;
        } while (page <= totalPages);

        return categories;
    }

    private extractTotalPages(value: unknown, depth = 0): number {
        if (!value || typeof value !== 'object' || depth >= 3) return 1;

        const record = value as Record<string, unknown>;
        const directValue = record.totalPages ?? record.total_pages;
        const directNumber = Number(directValue);

        if (Number.isFinite(directNumber) && directNumber > 0) {
            return directNumber;
        }

        for (const key of ['page', 'data', 'pagination', 'meta']) {
            if (!(key in record)) continue;

            const nested = this.extractTotalPages(record[key], depth + 1);
            if (nested > 1) return nested;
        }

        return 1;
    }

    private mapLegacyCategory(category: unknown): CategoryOption | null {
        const item = category as {
            uuid?: string;
            id?: string;
            externalId?: string;
            external_id?: string;
            name?: string | { name?: string; namePl?: string };
            localizedName?: { name?: string; namePl?: string };
            useAsComponent?: boolean;
            use_as_component?: boolean;
            isComponent?: boolean;
            is_component?: boolean;
            type?: string;
            categoryType?: string;
            category_type?: string;
        };

        const uuid = this.asString(
            item.uuid ?? item.id ?? item.externalId ?? item.external_id,
        );
        const name =
            typeof item.name === 'string'
                ? this.asString(item.name)
                : this.asString(item.name?.name ?? item.localizedName?.name);
        const namePl = this.asString(
            typeof item.name === 'object'
                ? item.name?.namePl
                : item.localizedName?.namePl,
        );

        if (!uuid || (!name && !namePl)) return null;

        return {
            uuid,
            name: name || namePl,
            namePl: namePl || name,
            categorySource: 'LEGACY',
            legacyCategoryUuid: uuid,
            component: this.inferCategoryComponentFlag(item),
        };
    }

    private mapWarehouseCategory(category: unknown): CategoryOption | null {
        const item = category as {
            id?: string;
            uuid?: string;
            externalId?: string;
            external_id?: string;
            systemId?: string;
            system_id?: string;
            name?: string | { name?: string; namePl?: string };
            localizedName?: { name?: string; namePl?: string };
            label?: string;
            namePl?: string;
            type?: string;
            categoryType?: string;
            category_type?: string;
            status?: string;
        };

        const type = this.extractEnumValue(
            item.type ?? item.categoryType ?? item.category_type,
        ).toUpperCase();
        const status = this.extractEnumValue(
            item.status ?? 'ACTIVE',
        ).toUpperCase();

        // Imported inventory rows are regular products (`useAsComponent: false`),
        // so component-only warehouse categories must not be selected.
        const isProductCategory =
            !type ||
            type === 'PRODUCT' ||
            type === 'PRODUCT_CATEGORY' ||
            type === 'PRODUCTS';

        if (
            !isProductCategory ||
            status === 'INACTIVE' ||
            status === 'DELETED'
        ) {
            return null;
        }

        const uuid = this.asString(
            item.id ??
                item.uuid ??
                item.externalId ??
                item.external_id ??
                item.systemId ??
                item.system_id,
        );
        const name =
            typeof item.name === 'string'
                ? this.asString(item.name)
                : this.asString(
                      item.name?.name ??
                          item.localizedName?.name ??
                          item.label,
                  );
        const namePl = this.asString(
            typeof item.name === 'object'
                ? item.name?.namePl
                : (item.localizedName?.namePl ?? item.namePl),
        );

        if (!uuid || (!name && !namePl)) return null;

        return {
            uuid,
            name: name || namePl,
            namePl: namePl || name,
            categorySource: 'WAREHOUSE',
            warehouseCategoryUuid: uuid,
            component: false,
        };
    }

    private extractEnumValue(value: unknown): string {
        if (value && typeof value === 'object') {
            const item = value as Record<string, unknown>;
            const candidate =
                item.value ?? item.id ?? item.code ?? item.name ?? item.label;

            if (candidate !== value && typeof candidate === 'object') {
                return this.extractEnumValue(candidate);
            }

            return this.asString(candidate);
        }

        return this.asString(value);
    }

    private inferCategoryComponentFlag(
        category: unknown,
    ): boolean | undefined {
        const item = category as Record<string, unknown>;
        const explicitComponent =
            item.useAsComponent ??
            item.use_as_component ??
            item.isComponent ??
            item.is_component;

        if (explicitComponent !== undefined) {
            return Boolean(explicitComponent);
        }

        const type = this.asString(
            item.type ?? item.categoryType ?? item.category_type,
        ).toUpperCase();

        if (type === 'COMPONENT') return true;
        if (type === 'PRODUCT') return false;
        return undefined;
    }

    private mergeCategories(
        legacyCategories: CategoryOption[],
        warehouseCategories: CategoryOption[],
    ): CategoryOption[] {
        const merged = [...warehouseCategories];

        for (const legacyCategory of legacyCategories) {
            const warehouseIndex = merged.findIndex(
                (candidate) =>
                    candidate.categorySource === 'WAREHOUSE' &&
                    this.categoryNamesMatch(candidate, legacyCategory),
            );

            if (warehouseIndex === -1) {
                merged.push(legacyCategory);
                continue;
            }

            const warehouseCategory = merged[warehouseIndex];

            // As in ProductSidebar, a name match represents the same logical
            // category. Prefer the legacy UUID for the inventory API while
            // retaining the warehouse UUID as metadata.
            merged[warehouseIndex] = {
                ...warehouseCategory,
                uuid: legacyCategory.uuid,
                name: legacyCategory.name || warehouseCategory.name,
                namePl: legacyCategory.namePl || warehouseCategory.namePl,
                categorySource: 'MERGED',
                legacyCategoryUuid: legacyCategory.uuid,
                warehouseCategoryUuid: warehouseCategory.uuid,
                component: legacyCategory.component ?? false,
            };
        }

        const deduplicated = new Map<string, CategoryOption>();

        for (const category of merged) {
            const names = [category.name, category.namePl]
                .map((name) => this.normalizeComparable(name))
                .filter(Boolean)
                .sort()
                .join('::');
            const key = `${category.categorySource}::${category.uuid}::${names}`;

            if (!deduplicated.has(key)) {
                deduplicated.set(key, category);
            }
        }

        return Array.from(deduplicated.values());
    }

    private categoryNamesMatch(
        left: CategoryOption,
        right: CategoryOption,
    ): boolean {
        const leftNames = new Set(
            [left.name, left.namePl]
                .map((name) => this.normalizeComparable(name))
                .filter(Boolean),
        );

        return [right.name, right.namePl]
            .map((name) => this.normalizeComparable(name))
            .filter(Boolean)
            .some((name) => leftNames.has(name));
    }

    private async fetchUnitOfMeasurementOptions(): Promise<DictionaryOption[]> {
        try {
            const response = await this.fetchReferenceCollection(
                'getUnitOfMeasurementList',
            );

            return this.mapDictionaryOptions(response, [
                'code',
                'value',
                'id',
                'name',
                'label',
            ]);
        } catch (error) {
            console.error(
                'ProductImportService::fetchUnitOfMeasurementOptions error',
                error,
            );
            return [];
        }
    }

    private async fetchVatCodeOptions(): Promise<DictionaryOption[]> {
        try {
            const response = await this.fetchReferenceCollection(
                'getVatCodeList',
            );

            return this.mapDictionaryOptions(response, [
                'code',
                'value',
                'id',
                'name',
                'label',
            ]);
        } catch (error) {
            console.error(
                'ProductImportService::fetchVatCodeOptions error',
                error,
            );
            return [];
        }
    }

    private async fetchCurrencyOptions(): Promise<DictionaryOption[]> {
        try {
            const response = await this.fetchReferenceCollection(
                'getCurrencyList',
            );

            return this.mapDictionaryOptions(response, [
                'code',
                'value',
                'id',
                'name',
                'label',
            ]);
        } catch (error) {
            console.error(
                'ProductImportService::fetchCurrencyOptions error',
                error,
            );
            return [];
        }
    }

    private async fetchStatusOptions(): Promise<DictionaryOption[]> {
        try {
            const response = await this.fetchReferenceCollection(
                'getStatusList',
            );

            return this.mapDictionaryOptions(response, [
                'value',
                'code',
                'id',
                'name',
                'label',
            ]);
        } catch (error) {
            console.error(
                'ProductImportService::fetchStatusOptions error',
                error,
            );
            return [];
        }
    }

    private mapDictionaryOptions(
        values: unknown[],
        idKeys: string[],
    ): DictionaryOption[] {
        return values
            .map((value): DictionaryOption | null => {
                if (
                    typeof value === 'string' ||
                    typeof value === 'number'
                ) {
                    const normalized = this.asString(value);
                    if (!normalized) return null;

                    return {
                        id: normalized.toUpperCase(),
                        label: normalized,
                    };
                }

                if (!value || typeof value !== 'object') return null;

                const item = value as Record<string, unknown>;
                const id =
                    idKeys
                        .map((key) => this.extractEnumValue(item[key]))
                        .find(Boolean) ?? '';
                const label = this.extractEnumValue(
                    item.label ??
                        item.displayName ??
                        item.name ??
                        item.code ??
                        item.value ??
                        id,
                );

                if (!id) return null;

                return {
                    id: id.toUpperCase(),
                    label: label || id,
                };
            })
            .filter((option): option is DictionaryOption => option !== null);
    }

    private async fetchReturnablePackagingOptions(): Promise<
        ReturnablePackagingOption[]
    > {
        try {
            const { ReturnablePackagingService } = await import(
                '../../../service/ReturnablePackagingService'
            );

            const items: Array<{
                packaging?: {
                    uuid?: string;
                    name?: { name?: string; namePl?: string };
                };
                price?: number;
            }> = (await ReturnablePackagingService.list()) ?? [];

            return items
                .map((item) => {
                    const uuid = this.asString(item.packaging?.uuid);
                    const label = this.asString(item.packaging?.name?.name);
                    const labelPl =
                        this.asString(item.packaging?.name?.namePl) || label;
                    const price = item.price ?? null;

                    if (!uuid || !label) return null;

                    return { uuid, label, labelPl, price };
                })
                .filter((o): o is ReturnablePackagingOption => o !== null);
        } catch (error) {
            console.error(
                'ProductImportService::fetchReturnablePackagingOptions error',
                error,
            );
            return [];
        }
    }

    private async parseFile(
        file: File,
        dictionaries: ImportDictionaries,
    ): Promise<ProductImportRow[]> {
        const buffer = await file.arrayBuffer();

        const workbook = XLSX.read(buffer, { type: 'array', cellDates: true });

        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];

        const rawRows = XLSX.utils.sheet_to_json<Record<string, unknown>>(
            sheet,
            {
                defval: '',
                raw: false,
                range: 4,
            },
        );

        const rows = rawRows.filter((row) =>
            Object.values(row).some((v) => this.asString(v) !== ''),
        );

        return rows.map((row, index) => this.mapRow(row, index, dictionaries));
    }

    private mapRow(
        row: Record<string, unknown>,
        index: number,
        dictionaries: ImportDictionaries,
    ): ProductImportRow {
        const n = this.normalizeRow(row);

        const externalId = this.asString(
            n.idzewnetrzne ?? n.externalid ?? n.externalsystemid ?? n.id,
        );

        const ean = this.asString(n.ean ?? n.barcode);

        const name = this.asString(
            n.nazwaproduktu ?? n.name ?? n.product ?? n.productname,
        );

        const categoryName = this.asString(
            n.kategoria ?? n.category ?? n.categoryname,
        );

        const weightVolume = this.toNumber(
            n.wagaobjetosc ?? n.weightvolume ?? n.weight ?? n.volume,
        );

        const rawUnitOfMeasurement = this.asString(
            n.jednostkamiary ?? n.unitofmeasurement ?? n.unit ?? n.uom ?? n.udm,
        );

        const unitOfMeasurement = rawUnitOfMeasurement.toUpperCase();

        const minQuantity = this.toNumber(n.minilosc ?? n.minquantity ?? n.min);

        const maxQuantity = this.toNumber(n.maxilosc ?? n.maxquantity ?? n.max);

        const rawPriceValue = n.cena ?? n.price;
        const rawPrice = this.asString(rawPriceValue);
        const price = this.toNumber(rawPriceValue);
        const isInvalidPrice = rawPrice !== '' && price === null;

        const rawCurrency = this.asString(n.waluta ?? n.currency);
        const currency = rawCurrency.toUpperCase();

        const rawVatCode = this.asString(n.kodvat ?? n.vatcode);
        const vatCode = this.extractVatCodeLetter(rawVatCode);

        const rawStatus = this.asString(n.status ?? n.statusproduktu);
        const status = rawStatus ? rawStatus.toUpperCase() : 'ACTIVE';

        const matchedCategory = this.resolveCategory(
            categoryName,
            dictionaries.categories,
        );

        const matchedUnitOfMeasurement = this.resolveDictionaryOption(
            unitOfMeasurement,
            dictionaries.unitOfMeasurementOptions,
        );

        const matchedVatCode = this.resolveDictionaryOption(
            vatCode,
            dictionaries.vatCodeOptions,
        );

        const matchedCurrency = this.resolveDictionaryOption(
            currency,
            dictionaries.currencyOptions,
        );

        const matchedStatus = this.resolveDictionaryOption(
            status,
            dictionaries.statusOptions,
        );

        const categoryUuid =
            matchedCategory?.categorySource === 'WAREHOUSE'
                ? (matchedCategory.warehouseCategoryUuid ??
                    matchedCategory.uuid)
                : (matchedCategory?.legacyCategoryUuid ??
                    matchedCategory?.uuid ??
                    '');
        const categoryNameEn = matchedCategory?.name ?? categoryName;
        const categoryNamePl = matchedCategory?.namePl ?? categoryName;

        const rawReturnablePackaging = this.asString(
            n.opakowaniezwrotne ?? n.returnablepackaging,
        ).toUpperCase();

        const returnablePackaging =
            rawReturnablePackaging === 'TAK' ||
            rawReturnablePackaging === 'YES' ||
            rawReturnablePackaging === 'TRUE'
                ? true
                : rawReturnablePackaging === 'NIE' ||
                    rawReturnablePackaging === 'NO' ||
                    rawReturnablePackaging === 'FALSE'
                  ? false
                  : null;

        const rawReturnablePackagingType = this.asString(
            n.typopakowaniazwrotnego ?? n.returnablepackagingtype,
        );

        const resolvedPackaging =
            returnablePackaging === true && rawReturnablePackagingType
                ? this.resolveReturnablePackaging(
                      rawReturnablePackagingType,
                      dictionaries.returnablePackagingOptions,
                  )
                : null;

        const returnablePackagingUuid = resolvedPackaging?.uuid ?? null;
        const returnablePackagingPrice = resolvedPackaging?.price ?? null;

        const errors: string[] = [];

        if (!name) {
            errors.push(IMPORT_ERRORS.missingProductName);
        }

        if (!ean && !externalId) {
            errors.push(IMPORT_ERRORS.missingEanOrExternalId);
        }

        if (!categoryName) {
            errors.push(IMPORT_ERRORS.missingCategory);
        } else if (!matchedCategory) {
            errors.push(
                this.translationError(IMPORT_ERRORS.categoryNotFound, {
                    category: categoryName,
                }),
            );
        }

        if (!unitOfMeasurement) {
            errors.push(IMPORT_ERRORS.missingUnitOfMeasurement);
        } else if (
            dictionaries.unitOfMeasurementOptions.length > 0 &&
            !matchedUnitOfMeasurement
        ) {
            errors.push(
                this.translationError(IMPORT_ERRORS.unitOfMeasurementNotFound, {
                    unitOfMeasurement,
                }),
            );
        }

        if (!currency) {
            errors.push(IMPORT_ERRORS.missingCurrency);
        } else if (
            dictionaries.currencyOptions.length > 0 &&
            !matchedCurrency
        ) {
            errors.push(
                this.translationError(IMPORT_ERRORS.currencyNotFound, {
                    currency,
                }),
            );
        }

        if (!vatCode) {
            errors.push(IMPORT_ERRORS.missingVatCode);
        } else if (
            dictionaries.vatCodeOptions.length > 0 &&
            !matchedVatCode
        ) {
            errors.push(
                this.translationError(IMPORT_ERRORS.vatCodeNotFound, {
                    vatCode,
                }),
            );
        }

        if (
            rawStatus &&
            dictionaries.statusOptions.length > 0 &&
            !matchedStatus
        ) {
            errors.push(
                this.translationError(IMPORT_ERRORS.statusNotFound, {
                    status,
                }),
            );
        }

        if (isInvalidPrice) {
            errors.push(
                this.translationError(IMPORT_ERRORS.invalidPrice, {
                    price: rawPrice,
                }),
            );
        }

        if (returnablePackaging === true && !rawReturnablePackagingType) {
            errors.push(IMPORT_ERRORS.returnablePackagingTypeRequired);
        }

        if (
            returnablePackaging === true &&
            rawReturnablePackagingType &&
            !resolvedPackaging
        ) {
            errors.push(
                this.translationError(
                    IMPORT_ERRORS.returnablePackagingTypeNotFound,
                    {
                        type: rawReturnablePackagingType,
                    },
                ),
            );
        }

        return {
            id: externalId || ean || String(index + 1),
            externalId,
            ean,
            name,
            categoryUuid,
            categorySource: matchedCategory?.categorySource,
            categoryName,
            categoryNameEn,
            categoryNamePl,
            weightVolume,
            unitOfMeasurement,
            minQuantity,
            maxQuantity,
            price,
            currency,
            vatCode,
            status,
            returnablePackaging,
            returnablePackagingType: rawReturnablePackagingType || null,
            returnablePackagingUuid,
            returnablePackagingPrice,
            existingProductExternalId: undefined,
            importStatus: errors.length ? 'INVALID' : 'NEW',
            errors,
            raw: row,
        } as ProductImportRow;
    }

    private resolveCategory(
        categoryName: string,
        categories: CategoryOption[],
    ): CategoryOption | null {
        const normalized = this.normalizeComparable(categoryName);
        if (!normalized) return null;

        return (
            categories.find((cat) =>
                [
                    cat.uuid,
                    cat.legacyCategoryUuid,
                    cat.warehouseCategoryUuid,
                    cat.name,
                    cat.namePl,
                ].some(
                    (v) => this.normalizeComparable(v) === normalized,
                ),
            ) ?? null
        );
    }

    private resolveDictionaryOption(
        value: string,
        options: DictionaryOption[],
    ): DictionaryOption | null {
        const normalized = this.normalizeComparable(value);
        if (!normalized) return null;

        return (
            options.find((option) =>
                [option.id, option.label].some(
                    (v) => this.normalizeComparable(v) === normalized,
                ),
            ) ?? null
        );
    }

    private resolveReturnablePackaging(
        rawType: string,
        options: ReturnablePackagingOption[],
    ): ReturnablePackagingOption | null {
        const normalized = this.normalizeComparable(rawType);
        if (!normalized) return null;

        return (
            options.find((opt) =>
                [opt.label, opt.labelPl].some(
                    (v) => this.normalizeComparable(v) === normalized,
                ),
            ) ?? null
        );
    }

    private extractVatCodeLetter(value: string): string {
        if (!value) return '';

        const trimmed = value.trim();

        const parenthesisMatch = trimmed.match(/^([A-Z0-9_-]+)\s*\(/i);
        if (parenthesisMatch) {
            return parenthesisMatch[1].toUpperCase();
        }

        return trimmed.toUpperCase();
    }

    private findFileDuplicateKeys(rows: ProductImportRow[]): Set<string> {
        const counts = new Map<string, number>();

        rows.forEach((row) => {
            const key = this.getCollisionKey(row);
            if (!key) return;
            counts.set(key, (counts.get(key) ?? 0) + 1);
        });

        const duplicateKeys = new Set<string>();
        counts.forEach((count, key) => {
            if (count > 1) duplicateKeys.add(key);
        });

        return duplicateKeys;
    }

    private getCollisionKey(row: ProductImportRow): string | null {
        if (row.ean) return `EAN: ${row.ean}`;
        if (row.externalId) return `External ID: ${row.externalId}`;
        return null;
    }

    private async findExistingProduct(
        row: ProductImportRow,
    ): Promise<ExistingProductMatch> {
        if (!row.ean) return { exists: false };

        try {
            const response = await this.productInventoryService.list({
                ean: row.ean,
                first: 0,
                max: 1,
            } as unknown);

            const product = this.extractFirstProduct(response);
            if (!product) return { exists: false };

            return {
                exists: true,
                externalId: this.asString(
                    product.externalId ?? product.id ?? product.uuid,
                ),
            };
        } catch (error) {
            console.error('ProductImportService::findExistingProduct error', {
                ean: row.ean,
                error,
            });
            return { exists: false };
        }
    }

    private extractFirstProduct(
        response: unknown,
    ): Record<string, unknown> | null {
        if (Array.isArray(response)) return response[0] ?? null;

        if (response && typeof response === 'object') {
            const r = response as {
                data?: unknown;
                content?: unknown;
                items?: unknown;
            };

            if (Array.isArray(r.data)) return r.data[0] ?? null;
            if (Array.isArray(r.content)) return r.content[0] ?? null;
            if (Array.isArray(r.items)) return r.items[0] ?? null;
        }

        return null;
    }

    private extractFile(payload: ImportPayload): File | null {
        if (payload instanceof File) return payload;

        if (payload instanceof FormData) {
            const value = payload.get('file') ?? payload.get('files');
            return value instanceof File ? value : null;
        }

        if ('file' in payload && payload.file instanceof File) {
            return payload.file;
        }

        if ('files' in payload && Array.isArray(payload.files)) {
            return payload.files[0] ?? null;
        }

        if ('target' in payload && payload.target?.files?.length) {
            return payload.target.files[0];
        }

        return null;
    }

    private translationError(
        key: string,
        params: Record<string, string>,
    ): string {
        const query = new URLSearchParams(params).toString();
        return `${key}?${query}`;
    }

    private normalizeRow(
        row: Record<string, unknown>,
    ): Record<string, unknown> {
        return Object.keys(row).reduce<Record<string, unknown>>((acc, key) => {
            acc[this.normalizeHeader(key)] = row[key];
            return acc;
        }, {});
    }

    private normalizeHeader(value: string): string {
        return value
            .trim()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\*/g, '')
            .replace(/[\s\-_/]/g, '');
    }

    private normalizeComparable(value: unknown): string {
        return this.asString(value)
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
    }

    private asString(value: unknown): string {
        if (value === null || value === undefined) return '';
        return String(value).trim();
    }

    private toNumber(value: unknown): number | null {
        if (value === null || value === undefined || value === '') return null;

        const parsed = Number(String(value).replace(',', '.'));

        return Number.isFinite(parsed) ? parsed : null;
    }

    /**
     * Downloads the base product-import template and stamps a per-account
     * "Kategoria" dictionary into it (Słowniki sheet, column F) before
     * handing it back, so every merchant sees their own category names -
     * legacy + warehouse, merged exactly like in the import flow above -
     * instead of a generic static list.
     */
    async downloadTemplate(): Promise<Blob> {
        const response = await fetch(productImportTemplateUrl);

        if (!response.ok) {
            throw new Error('Failed to download product import template');
        }

        const templateBuffer = await response.arrayBuffer();

        let categoryNames: string[] = [];

        try {
            const categories = await this.fetchCategories();
            categoryNames = this.getUniqueSortedCategoryNames(categories);
        } catch (error) {
            console.error(
                'ProductImportService::downloadTemplate fetchCategories error',
                error,
            );
        }

        if (!categoryNames.length) {
            // No categories available (or the fetch failed) - ship the
            // template as-is rather than block the download.
            return new Blob([templateBuffer], { type: XLSX_MIME_TYPE });
        }

        return this.injectCategoriesIntoTemplate(templateBuffer, categoryNames);
    }

    /**
     * Prefers the Polish label (namePl) since the template itself is
     * entirely in Polish - only falling back to the English `name` for
     * categories that don't have one. Matching a typed/pasted value back
     * to a category at import time (resolveCategory, above) already checks
     * both name and namePl, so this only affects what merchants *see* in
     * the dictionary column, not what values are accepted.
     */
    private getUniqueSortedCategoryNames(
        categories: CategoryOption[],
    ): string[] {
        const seen = new Set<string>();
        const names: string[] = [];

        categories
            .map((category) => category.namePl || category.name)
            .filter((name): name is string => Boolean(name))
            // Polish collation, so ą/ć/ę/ł/ń/ó/ś/ź/ż sort where a Polish
            // speaker expects them rather than wherever the default locale
            // happens to place them.
            .sort((a, b) => a.localeCompare(b, 'pl'))
            .forEach((name) => {
                const key = this.normalizeComparable(name);
                if (seen.has(key)) return;
                seen.add(key);
                names.push(name);
            });

        return names;
    }

    /**
     * Writes categoryNames into the "Kategoria" column (F, starting at row
     * 2) of the Słowniki sheet by patching the worksheet XML directly,
     * instead of round-tripping the workbook through the `xlsx` (SheetJS)
     * writer.
     *
     * This matters: the community build of `xlsx` used elsewhere in this
     * file does not persist cell styling (fonts/fills/borders) on write, so
     * XLSX.read(...) + XLSX.write(...) on this template would silently
     * strip all of its formatting. Patching the XML in place keeps the
     * template pixel-identical to the designed .xlsx.
     *
     * Only rows that actually receive a category name are created and
     * styled with the zebra fill - unlike an earlier version of this
     * method, nothing is pre-styled ahead of time. That previously left a
     * large block of purple rows below the real data whenever a tenant had
     * fewer categories than the reserved capacity (e.g. 7 real categories
     * still rendering 60 colored rows). If there isn't enough room before
     * the notes block (rows 25-28) for the full category list, the notes
     * are pushed further down instead of truncating the list.
     *
     * Requires the `jszip` package (dynamically imported, matching the
     * lazy-load pattern already used for ReturnablePackagingService below).
     */
    private async injectCategoriesIntoTemplate(
        templateBuffer: ArrayBuffer,
        categoryNames: string[],
    ): Promise<Blob> {
        try {
            const { default: JSZip } = await import('jszip');
            const zip = await JSZip.loadAsync(templateBuffer);
            const dictionarySheetFile = zip.file(
                TEMPLATE_CATEGORY_DICTIONARY_SHEET_PATH,
            );

            if (!dictionarySheetFile) {
                console.error(
                    'ProductImportService::injectCategoriesIntoTemplate',
                    `${TEMPLATE_CATEGORY_DICTIONARY_SHEET_PATH} not found in template`,
                );
                return new Blob([templateBuffer], { type: XLSX_MIME_TYPE });
            }

            const dictionarySheetXml = await dictionarySheetFile.async('string');
            const patchedDictionaryXml = this.buildCategoryDictionarySheetXml(
                dictionarySheetXml,
                categoryNames,
            );

            zip.file(
                TEMPLATE_CATEGORY_DICTIONARY_SHEET_PATH,
                patchedDictionaryXml,
            );

            // Widen the Kategoria dropdown on the Produkty sheet (column D)
            // to cover exactly the injected category rows. Best-effort: if
            // this sheet is missing or its markup ever changes shape, the
            // dictionary column above is still written correctly - the
            // dropdown just falls back to whatever range shipped in the
            // template.
            const productSheetFile = zip.file(TEMPLATE_PRODUCT_SHEET_PATH);

            if (productSheetFile) {
                const productSheetXml = await productSheetFile.async('string');
                const lastCategoryRow =
                    TEMPLATE_CATEGORY_FIRST_ROW + categoryNames.length - 1;

                if (
                    TEMPLATE_CATEGORY_VALIDATION_FORMULA_PATTERN.test(
                        productSheetXml,
                    )
                ) {
                    // Note: every literal "$" below is doubled ("$$") on
                    // purpose - in a String.replace() replacement string,
                    // "$1"/"$2" are backreferences, so a literal "$2" (as
                    // in the Excel range "$F$2") has to be written "$$2" or
                    // it gets swallowed as a reference to capture group 2.
                    const patchedProductXml = productSheetXml.replace(
                        TEMPLATE_CATEGORY_VALIDATION_FORMULA_PATTERN,
                        `$1Słowniki!$$F$$2:$$F$$${lastCategoryRow}$2`,
                    );

                    zip.file(TEMPLATE_PRODUCT_SHEET_PATH, patchedProductXml);
                } else {
                    console.warn(
                        'ProductImportService::injectCategoriesIntoTemplate',
                        'Kategoria dropdown validation not found on the Produkty sheet in the expected format; leaving its range untouched.',
                    );
                }
            }

            const output = await zip.generateAsync({ type: 'arraybuffer' });

            return new Blob([output], { type: XLSX_MIME_TYPE });
        } catch (error) {
            console.error(
                'ProductImportService::injectCategoriesIntoTemplate error',
                error,
            );
            // Fall back to the un-personalized template rather than block
            // the download entirely.
            return new Blob([templateBuffer], { type: XLSX_MIME_TYPE });
        }
    }

    private buildCategoryDictionarySheetXml(
        sheetXml: string,
        categoryNames: string[],
    ): string {
        const [zebraStyleEven, zebraStyleOdd] =
            this.discoverZebraStyleIndices(sheetXml);

        const gap =
            TEMPLATE_NOTE_ROWS[0] - TEMPLATE_CATEGORY_FIRST_ROW;
        const shift = Math.max(0, categoryNames.length - gap);

        let xml = sheetXml;

        if (shift > 0) {
            // Push the notes block (and its merged cell ranges) down by
            // `shift` rows to make room for the longer category list.
            // Processed from the highest row number down so a shifted
            // value never collides with a not-yet-processed old row
            // number.
            [...TEMPLATE_NOTE_ROWS]
                .sort((a, b) => b - a)
                .forEach((oldRow) => {
                    const newRow = oldRow + shift;

                    xml = xml.replace(
                        new RegExp(`<row r="${oldRow}"`, 'g'),
                        `<row r="${newRow}"`,
                    );

                    ['A', 'B', 'C', 'D', 'E'].forEach((col) => {
                        xml = xml.replace(
                            new RegExp(`r="${col}${oldRow}"`, 'g'),
                            `r="${col}${newRow}"`,
                        );
                    });

                    xml = xml.replace(
                        new RegExp(`ref="A${oldRow}:E${oldRow}"`, 'g'),
                        `ref="A${newRow}:E${newRow}"`,
                    );
                });
        }

        const sheetDataMatch = xml.match(/<sheetData>([\s\S]*?)<\/sheetData>/);

        if (!sheetDataMatch) {
            console.error(
                'ProductImportService::buildCategoryDictionarySheetXml',
                '<sheetData> not found in template sheet XML',
            );
            return sheetXml;
        }

        const rowRegex = /<row r="(\d+)"[^>]*>[\s\S]*?<\/row>/g;
        const rowsByNumber = new Map<number, string>();
        let match: RegExpExecArray | null;

        while ((match = rowRegex.exec(sheetDataMatch[1])) !== null) {
            rowsByNumber.set(parseInt(match[1], 10), match[0]);
        }

        categoryNames.forEach((name, index) => {
            const row = TEMPLATE_CATEGORY_FIRST_ROW + index;
            const styleIndex =
                index % 2 === 0 ? zebraStyleEven : zebraStyleOdd;
            const cellXml = `<c r="${TEMPLATE_CATEGORY_COLUMN}${row}" s="${styleIndex}" t="inlineStr"><is><t>${this.escapeXml(name)}</t></is></c>`;

            const existingRow = rowsByNumber.get(row);

            if (existingRow) {
                rowsByNumber.set(
                    row,
                    existingRow.replace(/<\/row>$/, `${cellXml}</row>`),
                );
            } else {
                rowsByNumber.set(
                    row,
                    `<row r="${row}" spans="1:6">${cellXml}</row>`,
                );
            }
        });

        const newSheetDataInner = Array.from(rowsByNumber.entries())
            .sort(([a], [b]) => a - b)
            .map(([, rowXml]) => rowXml)
            .join('');

        xml = xml.replace(
            /<sheetData>[\s\S]*?<\/sheetData>/,
            `<sheetData>${newSheetDataInner}</sheetData>`,
        );

        const lastCategoryRow =
            TEMPLATE_CATEGORY_FIRST_ROW + categoryNames.length - 1;
        const lastNoteRow =
            TEMPLATE_NOTE_ROWS[TEMPLATE_NOTE_ROWS.length - 1] + shift;
        const maxRow = Math.max(lastCategoryRow, lastNoteRow);

        xml = xml.replace(
            /<dimension ref="[^"]*"\s*\/>/,
            `<dimension ref="A1:F${maxRow}"/>`,
        );

        return xml;
    }

    /**
     * Reads the real style index the template's own dictionary zebra
     * striping currently uses, by inspecting the A2/A3 cells of the
     * Słowniki sheet directly (falling back to B2/B3, then C2/C3, in case
     * column A's cells ever move). This deliberately avoids hardcoding a
     * literal style index - see the comment on TEMPLATE_CATEGORY_COLUMN
     * above for why a hardcoded index is unsafe.
     */
    private discoverZebraStyleIndices(sheetXml: string): [string, string] {
        const fallbackColumns = ['A', 'B', 'C'];

        for (const column of fallbackColumns) {
            const evenMatch = sheetXml.match(
                new RegExp(`<c r="${column}2" s="(\\d+)"`),
            );
            const oddMatch = sheetXml.match(
                new RegExp(`<c r="${column}3" s="(\\d+)"`),
            );

            if (evenMatch && oddMatch) {
                return [evenMatch[1], oddMatch[1]];
            }
        }

        console.warn(
            'ProductImportService::discoverZebraStyleIndices',
            'Could not find the dictionary zebra style on any of columns A-C; injected category cells will be unstyled.',
        );

        // "0" is the default/no-op style index in every OOXML style table -
        // cells still render (just without the zebra fill) rather than the
        // download failing outright.
        return ['0', '0'];
    }

    private escapeXml(value: string): string {
        return value
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&apos;');
    }
}